<template>
  <!-- Dialog containing the form -->
  <v-dialog
    v-model="dialogOpen"
    persistent
    max-width="1200px"
    transition="dialog-bottom-transition"
    :fullscreen="isMobile"
  >
    <v-toolbar dark color="primary" flat>
      <v-toolbar-title>{{ $t("nav.PartnerReportCreate.title") }}</v-toolbar-title>

      <v-spacer></v-spacer>
      <v-btn icon dark @click="close">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>
    <v-window v-model="window">
      <v-window-item :value="PartnerReportCreateDialogWindow.FORM">
        <partner-report-create-form
          :groupId="groupId"
          :vehicle.sync="selectedVehicle"
          @created="setReport"
          @shared="setSharedContent"
          @submit="submit"
        ></partner-report-create-form>
      </v-window-item>
      <v-window-item :value="PartnerReportCreateDialogWindow.CHANNEL_PICK">
        <v-card flat>
          <v-card-title>{{ $t("components.partner.PartnerReportCreateDialog.chooseChannel") }}</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" sm="4">
                <v-btn block color="primary" :disabled="!hasEmail" elevation="0" @click="sendViaEmail">
                  <v-icon left>mdi-email-outline</v-icon>
                  {{ $t("components.partner.PartnerReportCreateDialog.sendEmail") }}
                </v-btn>
              </v-col>
              <v-col cols="12" sm="4">
                <v-btn block color="primary" :disabled="!hasPhone" elevation="0" @click="sendViaSms">
                  <v-icon left>mdi-message-text-outline</v-icon>
                  {{ $t("components.partner.PartnerReportCreateDialog.sendSms") }}
                </v-btn>
              </v-col>
              <v-col cols="12" sm="4">
                <v-btn
                  v-if="sharedContent"
                  block
                  elevation="0"
                  @click="
                    () => {
                      sharedContent.copyUrl();
                      close();
                    }
                  "
                >
                  <v-icon left>mdi-content-copy</v-icon>
                  {{ $t("timeLine.ActivityTimeLineItemShare.btnText") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-window-item>
      <v-window-item :value="PartnerReportCreateDialogWindow.SEND">
        <v-card flat>
          <v-card-text>
            <preselect-template-card
              v-if="PartnerReportCreateDialogWindow.SEND === window"
              ref="templateCard"
              :templateKey="templateKey"
              :language="partner.language"
              :partnerId="partner.id"
              :from="[partner]"
              :context="context"
              :to="to"
              :refs="mailRefs"
              :channel="selectedChannel"
              @sent="sent"
              @send="send"
              :isTicketEnabled="true"
            />
          </v-card-text>
          <v-card-actions class="px-4">
            <v-btn v-if="sharedContent" @click="() => sharedContent.copyUrl()" :elevation="0">
              {{ $t("timeLine.ActivityTimeLineItemShare.btnText") }}
            </v-btn>
            <v-spacer> </v-spacer>
            <v-btn color="success" @click="send" elevation="0" :loading="isSendLoading">
              {{ $t("components.template.dialog.preview.send") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-window-item>
    </v-window>
  </v-dialog>
</template>

<script lang="ts">
import PartnerReportCreateForm, {
  PartnerReportCreateFormSubmit
} from "@/components/partner/PartnerReportCreateForm.vue";
import PreselectTemplateCard from "@/components/partner/PreselectTemplateCard.vue";
import { ConversationChannelEnum } from "@/lib/enum/conversation-channel.enum";
import { PreselectTemplateKeysEnum } from "@/lib/enum/preselect-template-keys.enum";
import { IDialog } from "@/lib/interfaces/dialog.interface";
import { IReference } from "@/models/reference.entity";
import { IReport } from "@/models/report.entity";
import { ISharedContent } from "@/models/shared-content.entity";
import { IVehicle } from "@/models/vehicle.entity";
import { BackendResourceEnum, ResourceEnum } from "@/store/enum/authResourceEnum";
import { PartnerModule } from "@/store/modules/partner";
import { Component, Prop, Ref, Vue, Watch } from "vue-property-decorator";

enum PartnerReportCreateDialogWindow {
  FORM = "FORM",
  CHANNEL_PICK = "CHANNEL_PICK",
  SEND = "SEND"
}

@Component({
  components: {
    PartnerReportCreateForm,
    PreselectTemplateCard
  }
})
export default class PartnerReportCreateDialog extends Vue implements IDialog {
  readonly PreselectTemplateKeysEnum = PreselectTemplateKeysEnum;
  readonly BackendResourceEnum = BackendResourceEnum;
  ResourceEnum = ResourceEnum;
  PartnerReportCreateDialogWindow = PartnerReportCreateDialogWindow;

  @Prop({ required: false })
  groupId?: string;

  @Prop({ required: false })
  vehicle?: IVehicle;

  selectedVehicle: IVehicle | null = this.vehicle || null;

  dialogOpen = false;
  window = PartnerReportCreateDialogWindow.FORM;

  sharedContent: ISharedContent | null = null;
  report: IReport | null = null;

  selectedChannel: ConversationChannelEnum = ConversationChannelEnum.EMAIL;

  isSendLoading = false;

  @Ref("templateCard")
  templateCard!: PreselectTemplateCard;

  get isMobile() {
    return this.$vuetify.breakpoint.smAndDown;
  }

  get partner() {
    return PartnerModule.partner;
  }

  get to(): string[] {
    if (this.selectedChannel === ConversationChannelEnum.SMS) {
      const phone = this.report?.customerContact?.phone;
      return phone ? [phone] : [];
    }
    const mail = this.report?.customerContact?.email;
    return mail ? [mail] : [];
  }

  get templateKey(): string {
    if (this.selectedChannel === ConversationChannelEnum.SMS) {
      return PreselectTemplateKeysEnum.REQUEST_REPORT_ATTACHMENT_SMS;
    }
    return PreselectTemplateKeysEnum.REQUEST_REPORT_ATTACHMENT_MAIL;
  }

  get hasEmail(): boolean {
    return !!this.report?.customerContact?.email;
  }

  get hasPhone(): boolean {
    return !!this.report?.customerContact?.phone;
  }

  get context() {
    return {
      vehicle: this.selectedVehicle,
      report: this.report,
      sharedContent: this.sharedContent,
      partner: this.partner,
      url: { sharing: this.sharedContent?.url }
    };
  }

  get mailRefs(): IReference[] {
    const refs: IReference[] = [];

    if (this.report?.id) {
      refs.push({
        refType: BackendResourceEnum.REPORT,
        refId: this.report.id
      });
    }

    return refs;
  }

  @Watch("vehicle")
  setSelectedVehicle() {
    if (this.selectedVehicle?.id !== this.vehicle?.id) {
      this.selectedVehicle = this.vehicle || null;
    }
  }

  @Watch("selectedVehicle")
  emitSelectVehicle() {
    if (this.selectedVehicle?.id !== this.vehicle?.id) {
      this.$emit("update:vehicle", this.selectedVehicle);
    }
  }

  setReport(report: IReport) {
    this.report = report;
  }

  setSharedContent(sharedContent: ISharedContent) {
    this.sharedContent = sharedContent;
  }

  setAttachmentRequest(attachmentRequest: any) {
    this.$log.debug(attachmentRequest);
  }

  open() {
    this.window = PartnerReportCreateDialogWindow.FORM;
    this.selectedChannel = ConversationChannelEnum.EMAIL;
    this.report = null;
    this.sharedContent = null;
    this.dialogOpen = true;
  }

  close() {
    if (
      this.window === PartnerReportCreateDialogWindow.CHANNEL_PICK ||
      this.window === PartnerReportCreateDialogWindow.SEND
    ) {
      if (this.report && this.sharedContent) {
        this.$router.push({
          name: "PartnerReportsDetailView",
          params: {
            partnerId: this.report.partnerId,
            reportId: this.report.id
          }
        });
      }
    }
    this.dialogOpen = false;
  }

  async send() {
    this.isSendLoading = true;
    await this.templateCard?.send();
    this.isSendLoading = false;
  }

  sent() {
    this.$log.debug("sent");
    this.$toast.success("👍");

    if (!this.report) {
      this.close();
      return;
    }

    this.$router.push({
      name: "PartnerReportsDetailView",
      params: {
        partnerId: this.report.partnerId,
        reportId: this.report.id
      }
    });
    this.close();
  }

  sendViaEmail() {
    this.selectedChannel = ConversationChannelEnum.EMAIL;
    this.window = PartnerReportCreateDialogWindow.SEND;
  }

  sendViaSms() {
    this.selectedChannel = ConversationChannelEnum.SMS;
    this.window = PartnerReportCreateDialogWindow.SEND;
  }

  submit(submit: PartnerReportCreateFormSubmit) {
    this.$log.debug(submit);

    if (submit.isNotifyUser) {
      this.window = PartnerReportCreateDialogWindow.CHANNEL_PICK;
    } else {
      this.$router.push({
        name: "PartnerReportsDetailView",
        params: {
          partnerId: submit.report.partnerId,
          reportId: submit.report.id
        }
      });
    }
  }
}
</script>
